import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FaArrowLeft, FaFileExcel, FaFilePdf } from "react-icons/fa";
import { getAllUsers } from "../../api/user";
import {
  updateOrderDeliveryStatus,
  getVehicles,
  getVehicleById,
} from "../../api/routeOptimization";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import "./RouteDetails.css";

const RouteDetails = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [route, setRoute] = React.useState(location.state?.route);
  const [assignedUser, setAssignedUser] = React.useState(null);
  const [vehiclePlate, setVehiclePlate] = React.useState(null);
  const [confirmModal, setConfirmModal] = React.useState(null);
  const [isUpdating, setIsUpdating] = React.useState(false);

  // Fetch assigned user info and vehicle details
  React.useEffect(() => {
    const fetchUserAndVehicle = async () => {
      // Fetch user
      if (route?.userId) {
        try {
          const usersResponse = await getAllUsers();
          const usersData = Array.isArray(usersResponse) ? usersResponse : [];
          const user = usersData.find((u) => u.username === route.userId);
          setAssignedUser(user || null);
        } catch (err) {
          console.error("Error fetching user:", err);
        }
      }

      // Fetch vehicle details
      if (route?.vehicleId) {
        try {
          const vehicleResponse = await getVehicleById(route.vehicleId);
          const plate =
            vehicleResponse?.vehicles?.vehicleplate ||
            vehicleResponse?.vehicleplate ||
            vehicleResponse?.vehiclePlate ||
            null;
          setVehiclePlate(plate);
          console.log("Vehicle response:", vehicleResponse, "Plate:", plate);
        } catch (err) {
          console.error("Error fetching vehicle:", err);
        }
      }
    };
    fetchUserAndVehicle();
  }, [route]);

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleString("en-GB", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Handle mark as not delivered
  const handleMarkAsNotDelivered = (order) => {
    setConfirmModal(order);
  };

  const confirmMarkAsNotDelivered = async () => {
    if (!confirmModal) return;

    setIsUpdating(true);
    try {
      await updateOrderDeliveryStatus([
        {
          orderId: confirmModal.salesHeaderNo,
          vehicleOptimizationId: confirmModal.orderId,
          isDelivered: false,
        },
      ]);

      // Update local state to reflect the change
      setRoute((prevRoute) => ({
        ...prevRoute,
        orders: prevRoute.orders.map((order) =>
          order.orderId === confirmModal.orderId
            ? { ...order, isDelivered: false }
            : order,
        ),
      }));

      setConfirmModal(null);
    } catch (error) {
      console.error("Error updating order status:", error);
    } finally {
      setIsUpdating(false);
    }
  };

  const cancelModal = () => {
    setConfirmModal(null);
  };

  // Export to Excel
  const exportToExcel = () => {
    if (!route.orders || route.orders.length === 0) return;

    const data = route.orders.map((order) => ({
      DeliverySequence: order.deliverySequence,
      OrderID: order.orderId,
      SalesHeaderNo: order.salesHeaderNo,
      CustomerName: order.customerName,
      Address: order.customerAddress,
      OrderDate: order.orderDate,
      ItemCount: order.itemCount,
      TotalAmount: Number(order.totalAmount || 0).toFixed(2),
      EstimatedArrival: order.estimatedArrival,
      VehiclePlate: vehiclePlate || "N/A",
      VehicleDescription: route.vehicleDescription || "N/A",
      AssignedUser: assignedUser?.username || "Unassigned",
      Status: order.isDelivered ? "Delivered" : "Pending",
    }));

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();

    // Define styles
    const headerStyle = {
      font: { name: "Arial", sz: 12, bold: true, color: { rgb: "FFFFFF" } },
      fill: { fgColor: { rgb: "81D4FA" } },
      alignment: { horizontal: "center", vertical: "center", wrapText: true },
      border: {
        top: { style: "medium", color: { rgb: "333333" } },
        bottom: { style: "medium", color: { rgb: "333333" } },
        left: { style: "medium", color: { rgb: "333333" } },
        right: { style: "medium", color: { rgb: "333333" } },
      },
    };

    const rowStyleEven = {
      font: { name: "Arial", sz: 11, color: { rgb: "333333" } },
      fill: { patternType: "solid", fgColor: { rgb: "E6F3F3" } },
      border: {
        top: { style: "thin", color: { rgb: "999999" } },
        bottom: { style: "thin", color: { rgb: "999999" } },
        left: { style: "thin", color: { rgb: "999999" } },
        right: { style: "thin", color: { rgb: "999999" } },
      },
    };

    const rowStyleOdd = {
      font: { name: "Arial", sz: 11, color: { rgb: "333333" } },
      fill: { patternType: "solid", fgColor: { rgb: "F0F8F8" } },
      border: {
        top: { style: "thin", color: { rgb: "999999" } },
        bottom: { style: "thin", color: { rgb: "999999" } },
        left: { style: "thin", color: { rgb: "999999" } },
        right: { style: "thin", color: { rgb: "999999" } },
      },
    };

    // Apply styles
    const range = XLSX.utils.decode_range(worksheet["!ref"]);
    for (let C = range.s.c; C <= range.e.c; ++C) {
      const cellAddress = XLSX.utils.encode_cell({ r: 0, c: C });
      worksheet[cellAddress].s = headerStyle;
    }

    for (let R = 1; R <= range.e.r; ++R) {
      for (let C = range.s.c; C <= range.e.c; ++C) {
        const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
        worksheet[cellAddress].s = R % 2 === 0 ? rowStyleEven : rowStyleOdd;
      }
    }

    // Auto-adjust column widths
    const colWidths = Object.keys(data[0]).map((key) => {
      const columnValues = data.map((row) => String(row[key]));
      const maxLength = Math.max(
        key.length,
        ...columnValues.map((val) => val.length),
      );
      return { wch: Math.min(maxLength + 4, 35) };
    });
    worksheet["!cols"] = colWidths;

    // Set row heights
    worksheet["!rows"] = [{ hpt: 25 }];
    for (let i = 1; i <= range.e.r; i++) {
      worksheet["!rows"][i] = { hpt: 20 };
    }

    XLSX.utils.book_append_sheet(workbook, worksheet, "Route Orders");
    XLSX.writeFile(
      workbook,
      `Route_${route.no}_${route.vehicleDescription}_Orders.xlsx`,
    );
  };

  // Export to PDF
  const exportToPDF = () => {
    if (!route.orders || route.orders.length === 0) return;

    try {
      const doc = new jsPDF({ unit: "mm" });

      // Title
      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");
      doc.text(`Route ${route.no} - ${route.vehicleDescription}`, 14, 20);

      // Route info
      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.text(`Vehicle Plate: ${vehiclePlate || "N/A"}`, 14, 28);
      doc.text(
        `Assigned to: ${assignedUser ? assignedUser.name : "Unassigned"}`,
        14,
        34,
      );
      doc.text(`Total Orders: ${route.orders.length}`, 14, 40);

      // Prepare table data
      const tableColumn = [
        "Seq",
        "Order ID",
        "Sales No",
        "Customer",
        "Address",
        "Items",
        "Amount (€)",
        "Est. Arrival",
      ];
      const tableRows = route.orders.map((order) => [
        order.deliverySequence,
        order.orderId,
        order.salesHeaderNo,
        order.customerName,
        order.customerAddress,
        order.itemCount,
        Number(order.totalAmount || 0).toFixed(2),
        order.estimatedArrival,
      ]);

      // Generate table
      autoTable(doc, {
        head: [tableColumn],
        body: tableRows,
        startY: 46,
        styles: {
          fontSize: 8,
          cellPadding: 3,
          overflow: "linebreak",
          halign: "left",
          valign: "middle",
        },
        headStyles: {
          fillColor: [129, 212, 250],
          textColor: [0, 0, 0],
          fontStyle: "bold",
          halign: "center",
        },
        alternateRowStyles: {
          fillColor: [246, 248, 250],
        },
        columnStyles: {
          0: { cellWidth: 12 },
          1: { cellWidth: 20 },
          2: { cellWidth: 22 },
          3: { cellWidth: 35 },
          4: { cellWidth: 40 },
          5: { cellWidth: 12 },
          6: { cellWidth: 20 },
          7: { cellWidth: 22 },
        },
        margin: { top: 46, left: 10, right: 10, bottom: 10 },
        theme: "grid",
        didDrawPage: function (data) {
          // Footer
          const pageCount = doc.internal.getNumberOfPages();
          doc.setFontSize(8);
          doc.text(
            `Page ${
              doc.internal.getCurrentPageInfo().pageNumber
            } of ${pageCount}`,
            doc.internal.pageSize.width / 2,
            doc.internal.pageSize.height - 10,
            { align: "center" },
          );
        },
      });

      doc.save(`Route_${route.no}_${route.vehicleDescription}_Orders.pdf`);
    } catch (err) {
      console.error("Failed to generate PDF:", err);
    }
  };

  if (!route) {
    return (
      <div className="route-details-container">
        <div className="route-details-empty">
          <p>No route data available. Please go back and select a route.</p>
          <button
            onClick={() => navigate(-1)}
            className="route-details-back-btn"
          >
            <FaArrowLeft /> Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="route-details-container">
      {/* Header */}
      <div className="route-details-header">
        <div className="route-details-header-top">
          <button
            onClick={() => navigate(-1)}
            className="route-details-back-btn"
            title="Go Back"
          >
            <FaArrowLeft /> Back
          </button>
          <h1 className="route-details-title">Route Details</h1>
          <div className="route-details-export-buttons">
            <button
              onClick={exportToExcel}
              className="route-details-export-btn route-details-export-excel"
              title="Export to Excel"
              disabled={!route.orders || route.orders.length === 0}
            >
              <FaFileExcel /> Excel
            </button>
            <button
              onClick={exportToPDF}
              className="route-details-export-btn route-details-export-pdf"
              title="Export to PDF"
              disabled={!route.orders || route.orders.length === 0}
            >
              <FaFilePdf /> PDF
            </button>
          </div>
        </div>
        <p className="route-details-subtitle">
          Order {route.no} - {route.vehicleDescription}
        </p>
      </div>

      {/* Main Content */}
      <div className="route-details-content">
        {/* Route Summary Card */}
        <div className="route-details-summary-card">
          <h2 className="route-details-section-title">Route Information</h2>
          <div className="route-details-grid">
            <div className="route-details-item">
              <span className="route-details-label">Order No</span>
              <span className="route-details-value">{route.no}</span>
            </div>
            <div className="route-details-item">
              <span className="route-details-label">Vehicle ID</span>
              <span className="route-details-value">{route.vehicleId}</span>
            </div>
            <div className="route-details-item">
              <span className="route-details-label">Vehicle Description</span>
              <span className="route-details-value">
                {route.vehicleDescription || "N/A"}
              </span>
            </div>
            <div className="route-details-item">
              <span className="route-details-label">Batch ID</span>
              <span className="route-details-value">
                {route.optimizationBatchId || "N/A"}
              </span>
            </div>
            <div className="route-details-item">
              <span className="route-details-label">Company Code</span>
              <span className="route-details-value">{route.companyCode}</span>
            </div>
            <div className="route-details-item">
              <span className="route-details-label">Date Optimized</span>
              <span className="route-details-value">
                {formatDate(route.dateOptimized)}
              </span>
            </div>
            <div className="route-details-item">
              <span className="route-details-label">Assigned User</span>
              <span className="route-details-value">
                {assignedUser ? assignedUser.name : "Unassigned"}
              </span>
            </div>
            <div className="route-details-item">
              <span className="route-details-label">Total Amount</span>
              <span className="route-details-value">
                $
                {route.orders
                  ?.reduce((sum, order) => sum + (order.totalAmount || 0), 0)
                  .toFixed(2) || "0.00"}
              </span>
            </div>
            <div className="route-details-item">
              <span className="route-details-label">Delivery Status</span>
              <span
                className={`route-details-status-badge ${
                  route.isDelivered ? "delivered" : "pending"
                }`}
              >
                {route.isDelivered ? "✓ Delivered" : "⏱ Pending"}
              </span>
            </div>
          </div>
        </div>

        {/* Orders Section */}
        <div className="route-details-orders-section">
          <h2 className="route-details-section-title">
            Orders ({route.orders?.length || 0})
          </h2>

          {route.orders && route.orders.length > 0 ? (
            <div className="route-details-orders-list">
              {route.orders.map((order, idx) => (
                <div key={idx} className="route-details-order-card">
                  <div className="route-details-order-header">
                    <div className="route-details-order-title">
                      <h3>{order.customerName}</h3>
                      <span className="route-details-order-seq">
                        Delivery #{order.deliverySequence}
                      </span>
                    </div>
                    <div className="route-details-order-status-group">
                      <span
                        className={`route-details-order-status ${
                          order.isDelivered ? "delivered" : "pending"
                        }`}
                      >
                        {order.isDelivered ? "✓ Delivered" : "⏱ Pending"}
                      </span>
                      <span className="route-details-order-arrival">
                        {order.estimatedArrival}
                      </span>
                    </div>
                  </div>

                  <div className="route-details-order-content">
                    <div className="route-details-order-info-grid">
                      <div className="route-details-order-info-item">
                        <span className="route-details-label">Order ID</span>
                        <span className="route-details-value">
                          {order.orderId}
                        </span>
                      </div>
                      <div className="route-details-order-info-item">
                        <span className="route-details-label">Sales No</span>
                        <span className="route-details-value">
                          {order.salesHeaderNo}
                        </span>
                      </div>
                      <div className="route-details-order-info-item">
                        <span className="route-details-label">Order Date</span>
                        <span className="route-details-value">
                          {order.orderDate}
                        </span>
                      </div>
                      <div className="route-details-order-info-item">
                        <span className="route-details-label">Item Count</span>
                        <span className="route-details-value">
                          {order.itemCount}
                        </span>
                      </div>
                    </div>

                    <div className="route-details-order-address">
                      <span className="route-details-label">Address</span>
                      <p className="route-details-address-text">
                        {order.customerAddress}
                      </p>
                    </div>

                    <div className="route-details-order-footer">
                      <div className="route-details-order-amount">
                        <span className="route-details-label">
                          Total Amount
                        </span>
                        <span className="route-details-amount-value">
                          ${order.totalAmount?.toFixed(2) || "0.00"}
                        </span>
                      </div>
                      <div className="route-details-order-arrival-time">
                        <span className="route-details-label">
                          Est. Arrival
                        </span>
                        <span className="route-details-time-value">
                          {order.estimatedArrival}
                        </span>
                      </div>
                    </div>

                    {order.isDelivered && (
                      <div className="route-details-order-actions">
                        <button
                          onClick={() => handleMarkAsNotDelivered(order)}
                          className="route-details-btn-mark-undelivered"
                          disabled={isUpdating}
                        >
                          Mark as Not Delivered
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="route-details-no-orders">
              <p>No orders assigned to this route.</p>
            </div>
          )}
        </div>
      </div>

      {/* Confirmation Modal */}
      {confirmModal && (
        <div className="route-details-modal-overlay">
          <div className="route-details-modal">
            <h3>Confirm Action</h3>
            <p>
              Are you sure that this order has <strong>NOT</strong> been
              delivered?
            </p>
            <div className="route-details-modal-details">
              <p>
                <strong>Customer:</strong> {confirmModal.customerName}
              </p>
              <p>
                <strong>Order ID:</strong> {confirmModal.salesHeaderNo}
              </p>
            </div>
            <div className="route-details-modal-actions">
              <button
                onClick={confirmMarkAsNotDelivered}
                className="route-details-modal-btn-confirm"
                disabled={isUpdating}
              >
                {isUpdating ? "Updating..." : "Yes, Mark as Not Delivered"}
              </button>
              <button
                onClick={cancelModal}
                className="route-details-modal-btn-cancel"
                disabled={isUpdating}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RouteDetails;
